<template>
  <v-menu
    ref="menu"
    v-model="open"
    offset-y
    left
    nudge-bottom="6"
    min-width="0"
    content-class="app-menu"
    role="none"
  >
    <template v-slot:activator="{ on, attrs }">
      <button
        ref="button"
        type="button"
        class="product-switch"
        aria-label="Ticketing, Produkt wechseln"
        :title="tooltipOn ? 'Produkt wechseln' : null"
        v-bind="attrs"
        v-on="on"
        @click="onActivatorClick"
      >
        <img src="/img/sima-star.png" alt="" width="16" height="16" class="product-switch__star" />
        <span class="product-switch__name">Ticketing</span>
        <app-icon name="chevUD" :size="12" class="product-switch__chev" />
      </button>
    </template>
    <div
      class="app-menu__list product-menu"
      :class="{ 'product-menu--touch': touch }"
      role="menu"
      aria-label="Produkt wechseln"
      @keydown.tab="onTab"
    >
      <div class="app-menu__header" role="presentation" aria-hidden="true">Produkt wechseln</div>
      <!-- aktuelles Produkt: oranger Kreis mit Haken wie in der Kerbe der Hub-Shell -->
      <button
        type="button"
        class="v-list-item app-menu__item product-menu__item"
        role="menuitemradio"
        aria-checked="true"
        @click="open = false"
        @keydown.enter.stop.prevent="open = false"
      >
        <span class="product-menu__tile" aria-hidden="true">
          <img src="/img/sima-star.png" alt="" width="16" height="16" class="product-menu__star" />
        </span>
        <span class="app-menu__text product-menu__name">Ticketing</span>
        <span class="product-menu__here" aria-hidden="true">
          <app-icon name="check" :size="10" stroke-width="3" />
        </span>
      </button>
      <a
        :href="mobilityHubUrl"
        class="v-list-item app-menu__item product-menu__item"
        role="menuitemradio"
        aria-checked="false"
        @keydown.enter.stop.prevent="$event.currentTarget.click()"
      >
        <span class="product-menu__tile" aria-hidden="true">
          <img src="/img/sima-star.png" alt="" width="16" height="16" class="product-menu__star" />
        </span>
        <span class="app-menu__text product-menu__name">Kontrolle</span>
      </a>
      <template v-if="version">
        <div class="app-menu__divider" role="separator"></div>
        <p class="product-menu__foot">
          <app-icon name="info" :size="14" class="product-menu__info" />
          <span>v{{ version }}<span class="app-sr-only"> (Version)</span></span>
        </p>
      </template>
    </div>
  </v-menu>
</template>

<script>
// Produktwechsel rechts in der Systemleiste (Kundenwunsch 13.10.2026: „oben rechts in der statusbar noch ein auswahl
// select option womit man zwischen Ticketing und kontrolle wechseln kann (kontrolle = mobility hub)“ und „dort soll wen
// man draufklickt auch die version der aktuellen ticketing version stehen“). Steht hinter der Suche.
// Knopf: Pille wie die Suche (Höhe --h-ctl, Kante --edge mit Schatten --sh-ctl), SIMA-Stern 16, „Ticketing“ 13/20 500
// und Doppelpfeil 12 in --ink-3; Name „Ticketing, Produkt wechseln“, Tooltip „Produkt wechseln“ nur mit Maus.
// Menü (app-menu, rechtsbündig unter dem Knopf, Breite 232, Touch 264): Kopf „Produkt wechseln“, Ticketing als aktueller
// Ort (menuitemradio, oranger Kreis mit Haken) und Kontrolle als Link zum Mobility Hub im selben Fenster (dort eigene
// Anmeldung; Strg/⌘-Klick öffnet einen neuen Tab), Zeilen mit Stern-Kachel 24 wie die Kerbe der Hub-Shell
// (AppHubNotch). Darunter abgesetzt nur die Version (package.json über VUE_APP_VERSION; Kundenwunsch 13.10.2026: „das
// SIMA.Ticketing Verwaltung gar nicht anzeigen nur die version“).
// Tastatur wie „Darstellung“ (AppThemeToggle): Enter, Leertaste oder Pfeil ab öffnen mit Fokus auf Ticketing, Pfeile
// wandern, Enter wählt, Esc schließt, Tab schließt und geht vom Knopf aus weiter. Nicht auf dem Handy (dort Mehr-Menü:
// „Zu Mobility Hub wechseln“ und Version). Keine Props, Ereignisse oder Slots.
import AppIcon from '../ui/AppIcon.vue';
import { raiseMenu, followMenuFocus, focusMenuItem } from '../ui/AppSelect.vue';
import { MOBILITY_HUB_URL } from '../../shell/navigation';
import { notePopup } from '../../shell/shellState';

export default {
  name: 'AppProductSwitch',
  components: { AppIcon },
  data() {
    return {
      open: false,
      mounted: false,
      version: process.env.VUE_APP_VERSION || '',
      mobilityHubUrl: MOBILITY_HUB_URL
    };
  },
  computed: {
    touch() {
      return !!(this.$dichte && this.$dichte.touch);
    },
    tooltipOn() {
      return this.mounted && !this.touch;
    }
  },
  watch: {
    open(isOpen) {
      notePopup(isOpen);
      if (isOpen && this.keyboardOpen) {
        focusMenuItem(this.$refs.menu, '[aria-checked="true"]');
      }
      this.keyboardOpen = false;
    }
  },
  created() {
    // nicht reaktiv: zuletzt mit der Tastatur geöffnet (Klick mit detail 0)
    this.keyboardOpen = false;
  },
  mounted() {
    this.mounted = true;
    raiseMenu(this.$refs.menu);
    followMenuFocus(this, this.$refs.menu, () => this.open);
  },
  beforeDestroy() {
    if (this.open) {
      notePopup(false);
    }
  },
  methods: {
    onActivatorClick(e) {
      this.keyboardOpen = !!e && e.detail === 0;
    },
    onTab() {
      this.open = false;
      const button = this.$refs.button;
      if (button && button.focus) {
        button.focus();
      }
    }
  }
};
</script>

<style scoped>
/* ---------------------------------------------------------------- Knopf */
.product-switch {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: none;
  height: var(--h-ctl);
  box-sizing: border-box;
  margin: 0;
  padding: 0 10px 0 9px;
  border: 1px solid var(--edge);
  border-radius: 9999px;
  background: var(--surface-raised);
  box-shadow: var(--sh-ctl);
  color: var(--ink);
  font-family: var(--font);
  font-size: 13px;
  line-height: 20px;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
html.dichte-touch .product-switch {
  padding: 0 14px 0 13px;
  font-size: 14px;
}
.product-switch__star {
  flex: none;
  display: block;
  width: 16px;
  height: 16px;
}
.product-switch__chev {
  flex: none;
  color: var(--ink-3);
}
:where(html:not(.dichte-touch)) .product-switch:hover,
.product-switch[aria-expanded="true"] {
  background: var(--hov);
  border-color: var(--edge-hover);
}
.product-switch:active {
  background: var(--press);
  border-color: var(--edge-hover);
}
:where(html:not(.dichte-touch)) .product-switch:hover .product-switch__chev,
.product-switch[aria-expanded="true"] .product-switch__chev {
  color: var(--ink-2);
}
.product-switch:focus {
  outline: none;
}
.product-switch:focus-visible {
  outline: 2px solid var(--link);
  outline-offset: 2px;
}
@media print {
  .product-switch {
    display: none;
  }
}

/* ---------------------------------------------------------------- Menü (im Portal; v-menu gibt der Fläche das
   Scope-Attribut dieser Komponente mit) */
.product-menu {
  width: 232px;
}
.product-menu--touch {
  width: 264px;
}
.app-menu .product-menu__item {
  justify-content: flex-start;
  gap: 10px;
  text-decoration: none;
}
.product-menu__tile {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  box-sizing: border-box;
  border: 1px solid var(--line);
  border-radius: var(--r-ctl);
  background: var(--surface-raised);
}
.product-menu__star {
  display: block;
  width: 16px;
  height: 16px;
}
.product-menu__name {
  flex: 1 1 auto;
}
/* Ort: oranger Kreis 16 mit weißem Haken 10 */
.product-menu__here {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--acc);
  color: #fff;
}
/* Version: klein und gedämpft, linksbündig mit (i) davor, oben und unten gleich viel Abstand (Kundenwunsch
   13.10.2026); nicht wählbar */
.product-menu__info {
  flex: none;
  color: var(--ink-3);
}
.product-menu__foot {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  /* mit dem Trenner darüber (4) und dem Polster der Liste darunter (4 + Kante) je 12 Abstand zum Text */
  padding: 8px 12px 7px;
  font-size: 12px;
  line-height: 16px;
  color: var(--ink-3);
  font-variant-numeric: tabular-nums;
}
</style>
